/**
 * Calculates relative time to a target date
 * @param targetDate - The target date to compare against current time
 * @param clockOffsetMs - Offset (ms) to add to Date.now() to approximate server time.
 *                        Obtained from `useServerTimeOffset`. Defaults to 0.
 * @returns A human-readable string describing the relative time
 */
export function getRelativeTime(targetDate: Date, clockOffsetMs = 0): string {
  const now = new Date(Date.now() + clockOffsetMs);
  const diffMs = targetDate.getTime() - now.getTime();

  // If in the past
  if (diffMs < 0) {
    const absDiff = Math.abs(diffMs);
    const seconds = Math.floor(absDiff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
  }

  // If in the future
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `in ${days} day${days > 1 ? "s" : ""}`;
  if (hours > 0) return `in ${hours} hour${hours > 1 ? "s" : ""}`;
  if (minutes > 0) return `in ${minutes} minute${minutes > 1 ? "s" : ""}`;
  return `in ${seconds} second${seconds > 1 ? "s" : ""}`;
}

/**
 * Formats a countdown to a target date showing full breakdown
 * @param targetDate - The target date
 * @param clockOffsetMs - Offset (ms) to add to Date.now() to approximate server time.
 *                        Obtained from `useServerTimeOffset`. Defaults to 0.
 * @returns A detailed countdown string (e.g., "2d 5h 30m 15s")
 */
export function getDetailedCountdown(targetDate: Date, clockOffsetMs = 0): string {
  const now = new Date(Date.now() + clockOffsetMs);
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs < 0) {
    return "Expired";
  }

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const remainingHours = hours % 24;
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (remainingHours > 0) parts.push(`${remainingHours}h`);
  if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);

  return parts.join(" ");
}

/**
 * Parses a datetime-local string to a Date object
 */
export function fromDateTimeLocalString(value: string): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return !isNaN(date.getTime()) ? date : undefined;
}

/**
 * Formats a date to ISO 8601 format for datetime-local input display
 * Returns: YYYY-MM-DDTHH:mm (local timezone, for input display)
 */
export function toDateTimeLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats a date to full ISO 8601 UTC format for API/backend validation
 * Returns: YYYY-MM-DDTHH:mm:ssZ (UTC with seconds and Z suffix)
 */
export function toISODateTimeString(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}
